/**
 * 图片前端压缩工具类
 * 用于门店手机上传前压缩图片，减少传输量
 * 
 * 策略：
 * - 最大宽度 1920px，高度 1080px
 * - 质量 0.8（JPEG）
 * - 输出格式 JPEG
 * - 弱网环境可调低质量
 */
export class ImageCompressor {
  constructor(options = {}) {
    this.maxWidth = options.maxWidth || 1920
    this.maxHeight = options.maxHeight || 1080
    this.quality = options.quality || 0.8
    this.fileType = options.fileType || 'image/jpeg'
  }

  /**
   * 压缩单张图片
   * @param {File} file 原始图片文件
   * @returns {Promise<File>} 压缩后的文件
   */
  async compress(file) {
    // 非图片或太小的文件不压缩
    if (!file.type.startsWith('image/')) return file
    if (file.size < 100 * 1024) return file // 小于100KB不压缩

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let { width, height } = img

          // 等比例缩放
          if (width > this.maxWidth || height > this.maxHeight) {
            const ratio = Math.min(this.maxWidth / width, this.maxHeight / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }

          canvas.width = width
          canvas.height = height

          const ctx = canvas.getContext('2d')
          // 白色背景（处理PNG透明图）
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, width, height)
          ctx.drawImage(img, 0, 0, width, height)

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressed = new File([blob], this._getNewName(file.name), {
                  type: this.fileType,
                  lastModified: Date.now()
                })
                const rate = ((blob.size / file.size) * 100).toFixed(0)
                console.log(`[压缩] ${file.name}: ${(file.size / 1024).toFixed(0)}KB → ${(blob.size / 1024).toFixed(0)}KB (${rate}%)`)
                resolve(compressed)
              } else {
                reject(new Error('压缩失败'))
              }
            },
            this.fileType,
            this.quality
          )
        }
        img.onerror = () => resolve(file) // 加载失败返回原文件
        img.src = e.target.result
      }
      reader.onerror = () => resolve(file) // 读取失败返回原文件
      reader.readAsDataURL(file)
    })
  }

  /**
   * 批量压缩
   * @param {File[]} files 图片文件数组
   * @param {Function} onProgress 进度回调 (current, total, filename)
   * @returns {Promise<File[]>}
   */
  async compressAll(files, onProgress) {
    const results = []
    for (let i = 0; i < files.length; i++) {
      if (onProgress) onProgress(i + 1, files.length, files[i].name)
      try {
        const compressed = await this.compress(files[i])
        results.push(compressed)
      } catch {
        results.push(files[i])
      }
    }
    return results
  }

  _getNewName(originalName) {
    const dotIndex = originalName.lastIndexOf('.')
    const name = dotIndex > 0 ? originalName.substring(0, dotIndex) : originalName
    return `${name}_compressed.jpg`
  }

  updateOptions(options) {
    if (options.maxWidth !== undefined) this.maxWidth = options.maxWidth
    if (options.maxHeight !== undefined) this.maxHeight = options.maxHeight
    if (options.quality !== undefined) this.quality = options.quality
    if (options.fileType !== undefined) this.fileType = options.fileType
  }
}

export default new ImageCompressor()
